import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "./constants/adminConstants";
import PasswordStrengthBar from "./PasswordStrengthBar";
import "./css/adminStyles.css";

function AdminForgotPassword() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("loginId");
  const [loginId, setLoginId] = useState(searchParams.get("loginId") || "");
  const [userId, setUserId] = useState(null);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("email");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [passwordError, setPasswordError] = useState("");
  const [checkingPassword, setCheckingPassword] = useState(false);
  const [methodSelectorOpen, setMethodSelectorOpen] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (timer > 0) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [timer]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loginId }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Account not found.");
        setLoading(false);
        return;
      }

      setUserId(data.userId);
      setMethods(data.methods);
      if (data.methods.includes("email")) setMethod("email");
      else setMethod("phone");
      setStep("password");
    } catch (err) {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordNext = async (e) => {
    e.preventDefault();
    setError("");
    setPasswordError("");

    if (!password) {
      setError("Please enter a new password.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setCheckingPassword(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/check-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, password }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.same) {
          setPasswordError("New password must be different from current password.");
          setCheckingPassword(false);
          return;
        }
      }
    } catch {
    }

    setCheckingPassword(false);
    setStep("otp");
  };

  const handleSendOtp = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, method }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to send OTP.");
        setLoading(false);
        return;
      }

      setOtpSent(true);
      setTimer(120);
      setMethodSelectorOpen(false);
      setMessage(`OTP sent to your ${method === "email" ? "registered email" : "registered phone number"}.`);
      setTimeout(() => document.getElementById("otp-input-0")?.focus(), 100);
    } catch (err) {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    }
  };

  const handleResend = () => { setTimer(0); handleSendOtp(); };

  const handleVerifyOtp = async () => {
    setError("");
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter the full 6-digit OTP.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, otp: otpString }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Invalid OTP.");
        setLoading(false);
        return;
      }

      const resetRes = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, password }),
      });

      const resetData = await resetRes.json();

      if (!resetRes.ok) {
        setError(resetData.error || "Failed to reset password.");
        setLoading(false);
        return;
      }

      setStep("done");
    } catch (err) {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "done") {
    return (
      <div className="AdminLoginPage">
        <div className="AdminLoginBox">
          <h1 className="AdminLoginTitle">Password Reset</h1>
          <p className="AdminLoginSubtitle">Your password has been updated successfully.</p>
          <a href="/admin/login" className="AdminButton" style={{ textAlign: "center", display: "block", textDecoration: "none" }}>
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="AdminLoginPage">
      <div className="AdminLoginBox">
        {step === "loginId" && (
          <>
            <p className="AdminLoginSubtitle">Sign in to your account</p>
            <form onSubmit={handleLookup}>
              <div className="AdminFormGroup">
                <label className="AdminLabel">Login ID</label>
                <input
                  className="AdminInput"
                  type="text"
                  placeholder="SamakPuri.AdminTVC.com"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  required
                />
              </div>
              {error && <p className="AdminError">{error}</p>}
              <button className="AdminButton" type="submit" disabled={loading}>
                {loading ? "Checking..." : "Continue"}
              </button>
            </form>
          </>
        )}

        {step === "password" && (
          <>
            <h1 className="AdminLoginTitle">Forgot Password</h1>
            <form onSubmit={handlePasswordNext}>
            <div className="AdminFormGroup">
              <label className="AdminLabel">New Password</label>
              <div className="AdminPwInputWrap">
                <input
                  className="AdminInput AdminPwInput"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                  required
                />
                <button type="button" className="AdminPwToggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              <PasswordStrengthBar password={password} />
              {passwordError && <p className="AdminFieldError" style={{ marginTop: 4 }}>{passwordError}</p>}
            </div>
            <div className="AdminFormGroup">
              <label className="AdminLabel">Confirm Password</label>
              <div className="AdminPwInputWrap">
                <input
                  className={`AdminInput AdminPwInput ${confirmPassword && password !== confirmPassword ? "AdminInputError" : ""}`}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button type="button" className="AdminPwToggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex={-1}>
                  {showConfirmPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="AdminFieldError">Passwords do not match.</p>
              )}
            </div>
            {error && <p className="AdminError">{error}</p>}
            <button className="AdminButton" type="submit" disabled={checkingPassword || !!passwordError}>
              {checkingPassword ? "Checking..." : "Continue"}
            </button>
          </form>
          </>
        )}

        {step === "otp" && (
          <>
            <h1 className="AdminLoginTitle">Forgot Password</h1>
            {methodSelectorOpen && methods.length > 1 && (
              <div className="AdminMethodToggle">
                <p className="AdminLoginSubtitle" style={{ marginBottom: 12 }}>
                  Verify via your registered
                </p>
                <div className="AdminToggle">
                  {methods.map((m) => (
                    <button
                      key={m}
                      className={`AdminToggleBtn ${method === m ? "AdminToggleBtnActive" : ""}`}
                      onClick={() => setMethod(m)}
                    >
                      {m === "email" ? "Email" : "Phone"}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button
                    className="AdminButton AdminButtonSmall"
                    onClick={handleSendOtp}
                    disabled={loading}
                    style={{ width: "auto" }}
                  >
                    {loading ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              </div>
            )}

            {methodSelectorOpen && methods.length === 1 && (
              <div className="AdminMethodToggle">
                <p className="AdminLoginSubtitle" style={{ marginBottom: 12 }}>
                  Verify via your registered {methods[0]}
                </p>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button
                    className="AdminButton"
                    onClick={handleSendOtp}
                    disabled={loading}
                    style={{ width: "auto" }}
                  >
                    {loading ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              </div>
            )}

            {otpSent && (
              <>
                <p className="AdminLoginSubtitle" style={{ marginBottom: 16 }}>{message}</p>
                <div className="AdminOtpBoxRow">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      id={`otp-input-${i}`}
                      className="AdminOtpBox"
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>
                {timer > 0 && <p className="AdminOtpTimer" style={{ textAlign: "center", width: "100%", margin: "8px 0" }}>{formatTime(timer)}</p>}
                {error && <p className="AdminError">{error}</p>}
                <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 12 }}>
                  {timer === 0 && (
                    <button className="AdminButton AdminButtonSecondary" onClick={handleResend} disabled={loading} style={{ width: "auto", padding: "10px 20px" }}>
                      {loading ? "Sending..." : "Resend"}
                    </button>
                  )}
                  <button
                    className="AdminButton"
                    onClick={handleVerifyOtp}
                    disabled={otp.join("").length !== 6 || loading}
                    style={{ width: "auto", padding: "10px 20px" }}
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        <div className="AdminTextCenter">
          <a href="/admin/login" className="AdminLink">Back to Login</a>
        </div>
      </div>
    </div>
  );
}

export default AdminForgotPassword;